import React, { useEffect } from 'react';

import { useRootSelector } from 'rootStore';
import {
    selectUserAuthState,
    updateAccount,
    UserAuthState,
} from 'common/features/store/duck/account/duck';
import { Routes } from 'routes';
import { toQueryString } from 'common/utils/queryString';
import { getUserClaimsFromBff } from 'common/api/bff';
import { useDispatch } from 'react-redux';
import { getAuthDomain } from 'common/api/auth/authDomain';

export const withAuth = (returnPath?: string) => {
    return function <P>(WrappedComponent: React.ComponentType<P>) {
        const EnhancedComponent = (props: React.PropsWithChildren<P>) => {
            const userAuthState = useRootSelector(selectUserAuthState);
            const dispatch = useDispatch<any>();

            useEffect(() => {
                if (userAuthState === UserAuthState.NOTAUTHENTICATED) {
                    dispatch(updateAccount({ userAuthState: UserAuthState.INITIAL }));
                    sendToLoginPage(returnPath);
                }
            }, [userAuthState, dispatch]);

            return <WrappedComponent {...props} />;
        };
        return EnhancedComponent;
    };
};

export const getUrlToLoginPage = (returnPath = '', otac?: string) => {
    const path = returnPath ? returnPath : location.pathname;
    const returnUrl = path + location.search;

    return `${Routes.Login}?${toQueryString({ returnUrl, otac })}`;
};

export const sendToLoginPage = (returnPath = '', otac?: string) => {
    location.assign(getUrlToLoginPage(returnPath, otac));
};

export const openLoginPage = (returnPath = '', otac?: string) => {
    window.open(getUrlToLoginPage(returnPath, otac), 'login');
};

const BFF_LOGOUT_PATH = '/bff/logout';

const getLogoutClaim = async () => {
    const { data: userClaims, response } = await getUserClaimsFromBff();

    return {
        logoutUrl: userClaims?.find((claim) => claim.type === 'bff:logout_url')?.value,
        status: response?.status,
    };
};

export const getLogoutUrl = async () => (await getLogoutClaim()).logoutUrl;

export const sendToLogoutPage = async (returnPath = '&returnUrl=%2Fshop') => {
    const { logoutUrl, status } = await getLogoutClaim();

    if (logoutUrl) {
        location.assign(logoutUrl + returnPath);
        return;
    }

    // An expired BFF session 401s on /bff/user, so the bff:logout_url claim (which
    // carries the sid) is unreachable and Sign Out would silently do nothing.
    // The BFF only validates the sid while a session still exists, so the sid-less
    // endpoint is safe here — and only here, hence the explicit 401 check.
    if (status === 401) {
        location.assign(`${BFF_LOGOUT_PATH}?${returnPath.replace(/^&/, '')}`);
    }
};

export const getManageSecurityUrls = () => {
    const domain = getAuthDomain(window.location);
    const changePasswordUrl = (process.env.REACT_APP_PASSWORD_CHANGE_URL || '').replace(
        'domain',
        domain
    );
    const manage2FAUrl = (process.env.REACT_APP_MANAGE_2FA_URL || '').replace('domain', domain);
    const manage2FA = `${manage2FAUrl}?returnUrl=${window.location.href}`;

    return [changePasswordUrl, manage2FA];
};
